import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BrushIcon from '@mui/icons-material/Brush';
import CloseIcon from '@mui/icons-material/Close';
import { SearchIcon } from 'src/icons/SearchIcon';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import { calcImageURL } from 'src/utils/url';
import { getSourceDeviceInfo, SourceDeviceInfo } from 'src/pages/PlannerPage/utils/device-utils';
import {
    DesignChangeScope,
    useGetDeviceDesignsQuery,
    useSetDeviceDesignMutation,
} from 'src/api/v1/design-ApiSlice';
import {
    applyDesignChange,
    Design,
    parseDesigns,
} from 'src/pages/PlannerPage/Planner/dialogs/ChangeDesignDialog/changeDesignActions';

interface ChangeDesignDialogProps {
    onClose: () => void;
    deviceId: number;
}

export function ChangeDesignDialog({ onClose, deviceId }: ChangeDesignDialogProps) {
    const [searchText, setSearchText] = useState('');
    const [selectedDesignId, setSelectedDesignId] = useState<number | null>(null);
    const [scope, setScope] = useState<DesignChangeScope>(DesignChangeScope.PRODUCT);
    const [notChangedProducts, setNotChangedProducts] = useState<string[] | null>(null);

    const sourceInfo: SourceDeviceInfo = useMemo(() => getSourceDeviceInfo(deviceId), [deviceId]);

    const { data, isFetching } = useGetDeviceDesignsQuery(deviceId);
    const [setDeviceDesign, { isLoading: isProcessing }] = useSetDeviceDesignMutation();

    const designs: Design[] = useMemo(
        () => (data ? parseDesigns(data.designs) : []),
        [data]
    );

    const filteredDesigns: Design[] = useMemo(() => {
        if (searchText === '') return designs;
        const search = searchText.toLowerCase();
        return designs.filter(d =>
            `${d.manufacturer} - ${d.color}`.toLowerCase().includes(search)
        );
    }, [designs, searchText]);

    const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    }, []);

    const runChange = useCallback(
        (checkNotChangedProducts: boolean) => {
            if (selectedDesignId == null) return;
            setDeviceDesign({
                deviceId,
                selectedDesignId,
                type: scope,
                checkNotChangedProducts,
            }).then((response) => {
                if (!response.data) return;
                const result = response.data;

                if (checkNotChangedProducts && result.notChangedProducts.length > 0) {
                    setNotChangedProducts(result.notChangedProducts);
                    return;
                }

                applyDesignChange(result, selectedDesignId);
                onClose();
            });
        },
        [deviceId, selectedDesignId, scope, setDeviceDesign, onClose]
    );

    const handleConfirm = useCallback(() => {
        runChange(true);
    }, [runChange]);

    const handleForceConfirm = useCallback(() => {
        runChange(false);
    }, [runChange]);

    return (
        <BaseDialog
            onClose={onClose}
            disableClose={isProcessing}
            title={ts('change_design_dialog_title', 'Design replace')}
            titleIcon={<BrushIcon />}
            maxWidth="sm"
            fullWidth
            actionsLeft={
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                        {ts('change_design_scope', 'Replace scope:')}
                    </Typography>
                    <Select
                        value={scope}
                        onChange={(e) => setScope(e.target.value as DesignChangeScope)}
                        size="small"
                        disabled={isProcessing}
                        sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                    >
                        <MenuItem value={DesignChangeScope.PRODUCT}>
                            {sourceInfo.isInGroup
                                ? ts('change_design_scope_group', 'This group')
                                : ts('change_design_scope_single', 'This device')}
                        </MenuItem>
                        <MenuItem value={DesignChangeScope.ROOM}>
                            {`${ts('change_design_scope_room', 'All in room')} "${sourceInfo.roomName}"`}
                        </MenuItem>
                        <MenuItem value={DesignChangeScope.FLOOR}>
                            {`${ts('change_design_scope_floor', 'All on floor')} "${sourceInfo.floorName}"`}
                        </MenuItem>
                        <MenuItem value={DesignChangeScope.PROJECT}>
                            {data?.project_id
                                ? `${ts('change_design_scope_project', 'All in project')} "${data.project_id}"`
                                : ts('change_design_scope_project', 'All in project')}
                        </MenuItem>
                    </Select>
                </Stack>
            }
            actions={
                notChangedProducts ? (
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" onClick={() => setNotChangedProducts(null)} disabled={isProcessing}>
                            {ts('change_design_go_back', 'Go back')}
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleForceConfirm} disabled={isProcessing}>
                            {ts('change_design_proceed', 'Yes')}
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="primary"
                        disabled={selectedDesignId == null || isProcessing}
                    >
                        {ts('change_design_confirm', 'OK')}
                    </Button>
                )
            }
        >
            <Stack spacing={2} sx={{ pt: 2 }}>
                {/* Source device */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: 'grey.100', flexShrink: 0 }}
                    >
                        <Box
                            component="img"
                            src={calcImageURL(data?.sourceProductImage ?? sourceInfo.image)}
                            alt={sourceInfo.productName}
                            sx={{ width: 36, height: 36, objectFit: 'contain' }}
                        />
                    </Stack>
                    <Typography variant="subtitle1">{sourceInfo.productName}</Typography>
                </Stack>

                {notChangedProducts ? (
                    /* Confirmation: some products have no matching design */
                    <Alert severity="warning">
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {ts(
                                'change_design_not_changed_warning',
                                'The following products have no matching design and will be removed:'
                            )}
                        </Typography>
                        <List dense disablePadding>
                            {notChangedProducts.map((name, idx) => (
                                <ListItem key={idx} disableGutters sx={{ py: 0 }}>
                                    <ListItemText primary={name} />
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                ) : (
                    <>
                        {/* Search */}
                        <TextField
                            placeholder={ts('change_design_search', 'Search for designs...')}
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={handleSearchChange}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchText && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchText('')}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                        {/* Design grid */}
                        {isFetching ? (
                            <Stack alignItems="center" sx={{ py: 4 }}>
                                <CircularProgress />
                            </Stack>
                        ) : filteredDesigns.length === 0 ? (
                            <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                                {ts('change_design_no_results', 'No designs found.')}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    maxHeight: 360,
                                    overflow: 'auto',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                    gap: 1,
                                }}
                            >
                                {filteredDesigns.map((design) => {
                                    const isSelected = selectedDesignId === design.id;
                                    return (
                                        <Stack
                                            key={design.id}
                                            alignItems="center"
                                            spacing={0.5}
                                            onClick={() => setSelectedDesignId(design.id)}
                                            sx={{
                                                cursor: 'pointer',
                                                p: 1,
                                                borderRadius: 1,
                                                border: '2px solid',
                                                borderColor: isSelected ? theme.palette.primary.main : 'transparent',
                                                backgroundColor: isSelected
                                                    ? alpha(theme.palette.primary.main, 0.1)
                                                    : 'transparent',
                                                ':hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                                },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={calcImageURL(design.image)}
                                                alt={`${design.manufacturer} - ${design.color}`}
                                                sx={{ width: 48, height: 48, objectFit: 'contain' }}
                                            />
                                            <Typography variant="caption" fontWeight="bold" textAlign="center">
                                                {design.manufacturer}
                                            </Typography>
                                            <Typography variant="caption" textAlign="center">
                                                {design.color}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}
            </Stack>
        </BaseDialog>
    );
}
